import {addResources, getNewResources, getProducerBuilding, Resource, Resources, sumResources} from "../enum/Resource";
import {TravianVersionWithFeatures} from "../Version";
import BuildingDataHandler from "../data/building/BuildingDataHandler";
import TravianVersion from "../enum/version";

export const boosterIndexToResource: Array<Resource> = [
    Resource.Wood,
    Resource.Clay,
    Resource.Iron,
    Resource.Crop,
    Resource.Crop,
];

export const boosterIndexToGid: Array<number> = [5, 6, 7, 8, 9];

export type VillageState = {
    resourceTypes: Array<Resource>,
    resourceLevel: Array<number>,
    boosterLevel: Array<number>,
    oasisBuilding: number,
    oases: Array<Resources>,
    waterworks: number,
}

export type Config = {
    initialVillageState: VillageState,
    travianFeatures: TravianVersionWithFeatures,
    /** Whether waterworks should be considered. */
    enableWaterworks: boolean,
    /** Whether upgrading non-crop fields should be considered. */
    enableWCIFields: boolean,
    /** Whether boosters for wood, clay and iron should be considered. */
    enableWCIBoosters: boolean,
    /** Assume bonus production from gold. */
    enableGoldProduction: boolean,
    /** Which oasis are available for annexation. */
    availableOases: Array<Resources>,
    /** Additional production from e.g. hero or other villages. */
    externalProduction: Resources,
    /** Maximum resource field level to consider */
    maxFieldLevel: number,
}

export enum StepAction {
    upgradeField,
    upgradeBooster,
    upgradeWaterworks,
    upgradeOasisBuilding,
    annectOasis,
}

export type Step = {
    action: StepAction.annectOasis,
    oasis: Resources,
} | {
    action: StepAction.upgradeWaterworks | StepAction.upgradeField | StepAction.upgradeBooster | StepAction.upgradeOasisBuilding,
    upgradeIndex: number,
    upgradeFrom: number,
    upgradeTo: number,
}

export type StepEvaluation = {
    step: Step,
    preSteps: Array<Step>,
    costResource: Resources,
    costTime: number,
}

export type StepEvaluationWithVillage = {
    step: StepEvaluation,
    village: VillageState,
    previousVillage: VillageState,
}

export type ROIInfo = {
    roi: number,
    totalCostSum: number,
    productionDelta: number,
}

export function calculateProduction(config: Config, village: VillageState): Resources {
    const travianFeatures = config.travianFeatures;

    const buildingDataHandler = new BuildingDataHandler(travianFeatures);
    const production: Resources = getNewResources();

    let boosts = getNewResources();

    // Add oases first, because waterworks does some rounding
    for (const oasis of village.oases) {
        boosts = addResources(boosts, oasis);
    }

    // Add waterworks, they actually don't boost oases but give a bonus based on oasis bonus,
    // and that's rounded to the nearest percent.
    for (const resource in boosts) {
        boosts[resource] += Math.round(boosts[resource] * (village.waterworks * 0.05));
    }

    // Finally, add booster buildings
    boosts = addResources(boosts, getNewResources(
        village.boosterLevel[0] * 5,
        village.boosterLevel[1] * 5,
        village.boosterLevel[2] * 5,
        village.boosterLevel[3] * 5 + village.boosterLevel[4] * 5,
    ));

    for (let i = 0; i < village.resourceLevel.length; i++) {
        const gid = getProducerBuilding(village.resourceTypes[i]);
        const level = village.resourceLevel[i];

        if (level === 0) {
            if (config.travianFeatures.version === TravianVersion.Legends) {
                production[village.resourceTypes[i]] += Math.round(2 * (config.travianFeatures.features.increasedResourceProduction ? 1.4 : 1.0)) * config.travianFeatures.features.speed;
            } else {
                production[village.resourceTypes[i]] += 2 * config.travianFeatures.features.speed;
            }
        } else {
            const levelData = buildingDataHandler.getGidLevel(gid, level);
            production[Resource.Wood] += levelData.effects.production1 ? Math.round(levelData.effects.production1 * (1 + boosts[Resource.Wood] / 100)) : 0;
            production[Resource.Clay] += levelData.effects.production2 ? Math.round(levelData.effects.production2 * (1 + boosts[Resource.Clay] / 100)) : 0;
            production[Resource.Iron] += levelData.effects.production3 ? Math.round(levelData.effects.production3 * (1 + boosts[Resource.Iron] / 100)) : 0;
            production[Resource.Crop] += levelData.effects.production4 ? Math.round(levelData.effects.production4 * (1 + boosts[Resource.Crop] / 100)) : 0;
        }
    }

    if (config.enableGoldProduction) {
        for (const resource in production) {
            production[resource] = Math.round(production[resource] * 1.25);
        }
    }

    // Deduct resource field crop consumption
    for (let i = 0; i < village.resourceLevel.length; i++) {
        if (village.resourceLevel[i] > 0) {
            const gid = getProducerBuilding(village.resourceTypes[i]);
            const levelData = buildingDataHandler.getGidLevel(gid, village.resourceLevel[i]);
            production[Resource.Crop] -= levelData.supply;
        }
    }

    // Deduct booster crop consumption
    for (let i = 0; i < 5; i++) {
        if (village.boosterLevel[i] > 0) {
            const levelData = buildingDataHandler.getGidLevel(boosterIndexToGid[i], village.boosterLevel[i]);
            production[Resource.Crop] -= levelData.supply;
        }
    }

    // Deduct oasis building crop consumption
    if (village.oasisBuilding > 0) {
        const gid = config.travianFeatures.version === TravianVersion.Legends ? 37 : 18;
        const levelData = buildingDataHandler.getGidLevel(gid, village.oasisBuilding);
        production[Resource.Crop] -= levelData.supply;
    }

    // Deduct waterworks crop consumption
    if (village.waterworks > 0) {
        const levelData = buildingDataHandler.getGidLevel(45, village.waterworks);
        production[Resource.Crop] -= levelData.supply;
    }

    for (const resource in config.externalProduction) {
        production[resource] += config.externalProduction[resource];
    }

    return production;
}

function orderOases(config: Config): Array<Resources> {
    const resourceTiles = getNewResources(0, 0, 0, 0);
    for (const resource of config.initialVillageState.resourceTypes) {
        resourceTiles[resource]++;
    }
    return config.availableOases.toSorted((a, b) => {
        const scoreA = Object.entries(a).map(([resource, bonus]: [Resource, number]) => resourceTiles[resource] * bonus).reduce((a, b) => a + b, 0);
        const scoreB = Object.entries(b).map(([resource, bonus]: [Resource, number]) => resourceTiles[resource] * bonus).reduce((a, b) => a + b, 0);
        return scoreB - scoreA;
    });
}

function generatePossibleSteps(config: Config, village: VillageState): Array<Step> {
    // Try upgrading each resource field
    const steps: Array<Step> = [];
    for (let i = 0; i < village.resourceLevel.length; i++) {
        // Go only to level 23 (Legends) and 20 (Kingdoms), as those are the max levels reachable with maximum amount of storages.
        if (village.resourceLevel[i] >= config.maxFieldLevel) {
            continue;
        }
        // Don't upgrade wood, clay, iron, if it's disabled
        if (village.resourceTypes[i] !== Resource.Crop && !config.enableWCIFields) {
            continue;
        }
        if (!steps.some(step => step.action === StepAction.upgradeField && step.upgradeFrom === village.resourceLevel[i] && village.resourceTypes[step.upgradeIndex] === village.resourceTypes[i])) {
            steps.push({
                action: StepAction.upgradeField,
                upgradeFrom: village.resourceLevel[i],
                upgradeTo: village.resourceLevel[i] + 1,
                upgradeIndex: i,
            })
        }
    }

    // 1. There exist 0-0-0-18 fields, and trying to upgrade Sawmill there might break the algorithm further down the line due to unfulfillable requirements and potentially divisions by zero.
    // 2. We allow disabling WCI boosters (that's the filter function)
    const availableResourceTypes = [... new Set(village.resourceTypes)].filter(type => type === Resource.Crop || config.enableWCIBoosters);

    // Try upgrading boosters
    for (let i = 0; i < 5; i++) {
        if (!availableResourceTypes.includes(boosterIndexToResource[i])) {
            continue;
        }
        // We try upgrading all levels, because it might be more efficient to upgrade e.g. clay pit from 8 to 10 to build brickyard level 3,
        // while it might not be the case for just brickyard level 1.
        for (let toLevel = village.boosterLevel[i] + 1; toLevel <= 5; toLevel++) {
            steps.push({
                action: StepAction.upgradeBooster,
                upgradeIndex: i,
                upgradeFrom: village.boosterLevel[i],
                upgradeTo: toLevel,
            })
        }
    }

    // Oasis order is actually possible to precalculate, so we just need to try the next oasis in line.
    // Note that this only works because crop is always the most frequent resource type. If Travian introduces fields
    // with fewer croplands than other resource tiles, this will need revision.
    // It only works, because croplands have two boosters, so they are naturally preferred to be upgraded first,
    // which makes their oases more lucrative at an earlier stage than the other tiles.
    const freeOases = orderOases(config).slice(village.oases.length);
    if (freeOases.length > 0 && village.oases.length < 3) {
        const oasis = freeOases[0];
        steps.push({
            action: StepAction.annectOasis,
            oasis: oasis,
        })
    }

    if (config.enableWaterworks) {
        // Try upgrading waterworks to any level. Same reasoning as for boosters - Waterworks has Hero's mansion level 10
        // as requirement, so there might be cases where (annex oasis + waterworks 5) is best, but just
        // (annex oasis + waterworks 1) would not be.
        for (let i = village.waterworks + 1; i <= 20; i++) {
            steps.push({
                action: StepAction.upgradeWaterworks,
                upgradeFrom: village.waterworks,
                upgradeTo: i,
                upgradeIndex: 0,
            })
        }
    }

    return steps;
}

function getStepPrerequisites(config: Config, village: VillageState, step: Step): Array<Step> {
    const steps: Array<Step> = [];
    switch (step.action) {
        case StepAction.upgradeField:
        default:
            return steps;
        case StepAction.upgradeBooster:
            // Determine location of the highest resource field with the correct resource for this booster.
            const myFieldIndices = village.resourceTypes
                .map((_, index) => index)
                .filter(index => village.resourceTypes[index] === boosterIndexToResource[step.upgradeIndex]);
            const highestFieldIndex = myFieldIndices
                .reduce((a, b) => village.resourceLevel[a] >= village.resourceLevel[b] ? a : b, myFieldIndices[0]);
            const highestLevel = village.resourceLevel[highestFieldIndex];

            // Inject required levels

            // Grain mill (3) needs level 5, all others need level 10
            const requiredLevel = step.upgradeIndex === 3 ? 5 : 10;
            for (let i = highestLevel + 1; i <= requiredLevel; i++) {
                steps.push({
                    action: StepAction.upgradeField,
                    upgradeIndex: highestFieldIndex,
                    upgradeFrom: i - 1,
                    upgradeTo: i,
                })
            }

            // Bakery also needs grain mill level 5
            if (step.upgradeIndex === 4) {
                for (let i = village.boosterLevel[3] + 1; i <= 5; i++) {
                    steps.push({
                        action: StepAction.upgradeBooster,
                        upgradeIndex: 3,
                        upgradeFrom: i - 1,
                        upgradeTo: i,
                    })
                }
            }

            return steps;
        case StepAction.annectOasis:
            const oasisIndex = village.oases.length;
            const requiredOasisBuildingLevel = (config.travianFeatures.version === TravianVersion.Legends ? [10, 15, 20] : [1, 10, 20])[oasisIndex];
            if (requiredOasisBuildingLevel > village.oasisBuilding) {
                steps.push({
                    action: StepAction.upgradeOasisBuilding,
                    upgradeIndex: 0,
                    upgradeFrom: village.oasisBuilding,
                    upgradeTo: requiredOasisBuildingLevel,
                })
            }
            return steps;
        case StepAction.upgradeWaterworks:
            // We should not get here, if there is no oases available, but just in case ...
            if (config.availableOases.length === 0) {
                return steps;
            }
            if (village.oasisBuilding < 10) {
                steps.push({
                    action: StepAction.upgradeOasisBuilding,
                    upgradeIndex: 0,
                    upgradeFrom: village.oasisBuilding,
                    upgradeTo: 10,
                });
            }
            if (village.oases.length < 1) {
                const availableOases = orderOases(config);
                steps.push({
                    action: StepAction.annectOasis,
                    oasis: availableOases[0],
                })
            }
            return steps;
    }
}

function calculateStepCost(config: Config, village: VillageState, step: Step): Resources {
    if (step.action === StepAction.annectOasis) {
        // Doesn't cost anything, building the oasis building is a prerequisite step.
        return getNewResources();
    }

    const buildingDataHandler = new BuildingDataHandler(config.travianFeatures);
    let resources = getNewResources();
    let gid: number;

    switch (step.action) {
        case StepAction.upgradeField:
            const resourceType = village.resourceTypes[step.upgradeIndex];
            gid = getProducerBuilding(resourceType);
            break;

        case StepAction.upgradeOasisBuilding:
            gid = config.travianFeatures.version === TravianVersion.Legends ? 37 : 18;
            break;

        case StepAction.upgradeWaterworks:
            gid = 45;
            break;

        case StepAction.upgradeBooster:
            gid = boosterIndexToGid[step.upgradeIndex];
            break;
    }

    for (let i = step.upgradeFrom + 1; i <= step.upgradeTo; i++) {
        resources = addResources(resources, buildingDataHandler.getGidLevel(gid, i).resourceCost);
    }

    return resources;
}

export function applyStep(config: Config, village: VillageState, step: Step): { evaluation: StepEvaluation, village: VillageState } {
    const preSteps = getStepPrerequisites(config, village, step);

    let costResource: Resources = getNewResources();
    let costTime: number = 0;

    for (const preStep of preSteps) {
        const { evaluation: { costResource: _costResource, costTime: _costTime }, village: _village } = applyStep(config, village, preStep);
        village = _village;
        costResource = addResources(costResource, _costResource);
        costTime += _costTime;
    }

    const productionBefore = calculateProduction(config, village);

    costResource = addResources(costResource, calculateStepCost(config, village, step));
    costTime += Object.values(costResource).reduce((a, b) => a + b, 0) / Object.values(productionBefore).reduce((a, b) => a + b, 0);

    let newVillage = JSON.parse(JSON.stringify(village)) as VillageState;
    switch (step.action) {
        case StepAction.upgradeField:
            newVillage.resourceLevel[step.upgradeIndex] = step.upgradeTo;
            break;
        case StepAction.upgradeBooster:
            newVillage.boosterLevel[step.upgradeIndex] = step.upgradeTo;
            break;
        case StepAction.upgradeWaterworks:
            newVillage.waterworks = step.upgradeTo;
            break;
        case StepAction.upgradeOasisBuilding:
            newVillage.oasisBuilding = step.upgradeTo;
            break;
        case StepAction.annectOasis:
            newVillage.oases.push(step.oasis);
            break;
    }

    return {
        evaluation: {
            step,
            preSteps,
            costResource,
            costTime,
        },
        village: newVillage,
    }
}

export function determineNextStep(config: Config, village: VillageState): null|{ evaluation: StepEvaluation, village: VillageState } {
    const possibleSteps = generatePossibleSteps(config, village);

    switch (possibleSteps.length) {
        case 1: return applyStep(config, village, possibleSteps[0]);
        case 0: return null;
    }

    let bestStep = possibleSteps[0];

    for (const step of possibleSteps.slice(1)) {
        // We apply the current best candidate and the current cursor candidate in both possible orders
        // The faster order has the better option first.
        let timeDifference = 0;
        let variantA = applyStep(config, village, bestStep);
        timeDifference += variantA.evaluation.costTime;
        variantA = applyStep(config, variantA.village, step);
        timeDifference += variantA.evaluation.costTime;

        let variantB = applyStep(config, village, step);
        timeDifference -= variantB.evaluation.costTime;
        variantB = applyStep(config, variantB.village, bestStep);
        timeDifference -= variantB.evaluation.costTime;

        if (timeDifference > 0) {
            bestStep = step;
        }
    }

    return applyStep(config, village, bestStep);
}

export type StepWithExplanation = {
    step: StepEvaluation,
    efficiency: number,
}

export function explainNextStep(config: Config, village: VillageState): null|Array<StepWithExplanation> {
    const nextStepData = determineNextStep(config, village);
    if (nextStepData === null) {
        return null;
    }

    const bestStep = nextStepData.evaluation.step;
    const possibleSteps = generatePossibleSteps(config, village);

    const variantACache = applyStep(config, village, bestStep);

    const detailedStepInformation: Array<StepWithExplanation> = [];
    for (const step of possibleSteps) {
        let timeA = variantACache.evaluation.costTime;
        const variantA = applyStep(config, variantACache.village, step);
        timeA += variantA.evaluation.costTime;

        let variantB = applyStep(config, village, step);
        let timeB = variantB.evaluation.costTime;
        variantB = applyStep(config, variantB.village, bestStep);
        timeB += variantB.evaluation.costTime;

        if (timeA === 0 || timeB === 0) {
            // Could happen if user input is something like "hero's mansion is level 20, but no oasis is annexed yet".
            // That produces an action "annex oasis for free", which obviously has no cost, and might lead to
            // division by zero.
            continue;
        }

        detailedStepInformation.push({
            step: applyStep(config, village, step).evaluation,
            efficiency: 1 - (timeB - timeA) / variantACache.evaluation.costTime,
        })
    }

    return detailedStepInformation;
}

export function calculateROI(config: Config, steps: Array<StepEvaluationWithVillage>): null|ROIInfo {
    if (steps.length === 0) {
        return null;
    }
    const productionBefore = sumResources(calculateProduction(config, steps[0].previousVillage));
    const productionAfter = sumResources(calculateProduction(config, steps[steps.length - 1].village));
    const totalCost = steps.reduce((a, b) => addResources(a, b.step.costResource), getNewResources());
    const totalCostSum = sumResources(totalCost);
    const productionDelta = productionAfter - productionBefore;

    if (productionDelta === 0) {
        return null;
    }

    return {
        roi: totalCostSum / productionDelta,
        totalCostSum,
        productionDelta,
    };
}
